/**
 * Lazy loader for three-csg-ts
 * 
 * three-csg-ts is lazily loaded to reduce initial bundle size.
 * The module is only loaded when CSG operations (cutouts, intersections) are needed.
 */

// Type for CSG class
export type CSGType = typeof import('three-csg-ts').CSG;

// Cached module reference
let cachedCSG: CSGType | null = null;
let loadPromise: Promise<CSGType> | null = null;

/**
 * Lazy load three-csg-ts module
 * Returns cached instance if already loaded
 * 
 * @returns Promise resolving to CSG class
 */
export const loadCSG = async (): Promise<CSGType> => {
	// Return cached if already loaded
	if (cachedCSG) {
		return cachedCSG;
	}

	// If already loading, wait for that promise
	if (loadPromise) {
		return loadPromise;
	}

	// Start loading
	loadPromise = (async () => {
		const module = await import(/* webpackChunkName: "three-csg" */ 'three-csg-ts');
		cachedCSG = module.CSG;
		return cachedCSG;
	})();

	return loadPromise;
};

/**
 * Check if CSG has been loaded
 */
export const isCSGLoaded = (): boolean => {
	return cachedCSG !== null;
};

/**
 * Get the cached CSG class (throws if not loaded)
 * Use this only after ensuring loadCSG() has completed
 */
export const getCSG = (): CSGType => {
	if (!cachedCSG) {
		throw new Error('CSG not loaded. Call loadCSG() first.');
	}
	return cachedCSG;
};

